'use client';

import React from 'react';
import styled from '@benzinga/themetron';
import { Icon } from '../Icon';
import { faChevronDown } from '@fortawesome/pro-solid-svg-icons/faChevronDown';
import { faChevronUp } from '@fortawesome/pro-solid-svg-icons/faChevronUp';

type Option = { key: string | number; name: string | number };

export interface SelectMenuProps {
  label?: string;
  trigger?: 'hover' | 'click';
  onChange?: (value: any) => void;
  open?: boolean;
  options?: Option[];
  optionElement?: (option: any) => React.ReactElement | null;
  selected?: string | number | null;
  position?: 'right' | 'left';
}

export const SelectMenu: React.FC<SelectMenuProps> = ({
  label = '',
  onChange,
  open = false,
  optionElement,
  options = [],
  position = 'left',
  selected,
  trigger = 'hover',
}) => {
  const handleChange = (value: string | number) => {
    onChange && onChange(value);
  };

  return (
    <Container className={`select-menu position-${position} ${open ? 'opened' : 'closed'} trigger-${trigger}`}>
      {label && (
        <div className="select-active-item">
          {selected || label}
          <div className="chevron-icons-container">
            <Icon className="chevron-icon up" icon={faChevronUp} />
            <Icon className="chevron-icon down" icon={faChevronDown} />
          </div>
        </div>
      )}
      <ul aria-orientation="vertical" className="select-menu-list" role="menu" tabIndex={-1}>
        {options?.length &&
          options.map((option, index) => {
            return (
              <li
                className={`select-menu-list-item ${selected === option.key ? 'selected' : 'unselected'}`}
                key={`${option.key}-${index}`}
                onClick={() => handleChange(option.key)}
                role="menuitem"
                tabIndex={-1}
              >
                {optionElement ? optionElement(option) : option.name}
              </li>
            );
          })}
      </ul>
    </Container>
  );
};

export default SelectMenu;

const Container = styled.div`
  &.select-menu {
    display: inline-block;
    text-align: left;
    min-width: 10rem;
    z-index: 20;
    position: absolute;

    &.opened {
      .select-menu-list {
        display: block;
      }
    }

    &:not(.opened) {
      &.trigger-hover {
        .select-menu-list {
          display: none;
        }
        &:hover {
          .select-menu-list {
            display: block;
          }
        }
      }
    }

    &.trigger-click {
      &.closed {
        .select-menu-list {
          display: none;
        }
      }
    }

    &.position-right {
      right: 0;
    }

    .select-active-item {
      width: 100%;
      display: flex;
      align-items: center;
      justify-content: space-between;
      font-weight: ${({ theme }) => theme.fontWeight.medium};
      background-color: ${({ theme }) => theme.colors.background};
      border: 1px solid ${({ theme }) => theme.colors.border};
      box-shadow: ${({ theme }) => theme.shadow.sm};
      font-size: ${({ theme }) => theme.fontSize.base};
      padding: 0.5rem 1rem;
      border-radius: 0.375rem;
      cursor: pointer;

      .chevron-icons-container {
        display: flex;
        flex-direction: column;
        margin-left: 0.5rem;
      }

      .chevron-icon {
        font-size: ${({ theme }) => theme.fontSize.xs};

        &.down {
          margin-top: -2px;
        }
      }
    }

    .select-menu-list {
      display: flex;
      flex-direction: column;
      padding-top: 0.25rem;
      padding-bottom: 0.25rem;
      box-shadow: ${({ theme }) => theme.shadow.default};
      background-color: ${({ theme }) => theme.colors.background};
      border-radius: ${({ theme }) => theme.borderRadius.default};
      border: 1px solid ${({ theme }) => theme.colors.border};
      min-width: 10rem;
      margin-top: 0.25rem;
      z-index: 20;
      list-style-type: none;
    }

    .select-menu-list-item {
      display: flex;
      align-items: center;
      padding: 0.25rem 1rem;
      min-height: 30px;
      font-size: ${({ theme }) => theme.fontSize.base};
      cursor: pointer;
      border-left: 2px solid transparent;

      &.selected {
        background-color: ${({ theme }) => theme.colors.backgroundActive};
        border-left-color: ${({ theme }) => theme.colorPalette.blue600};
      }

      &:hover {
        background-color: ${({ theme }) => theme.colors.backgroundActive};
      }

      &:focus {
        outline: none;
      }
    }
  }
`;
